import React from 'react';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import dayjs from 'dayjs';
import { KpiCard } from '../../../components/financeiro';
import { formatCurrency } from '../../../utils/money';
import { useFinanceiroTorneio } from '../../../hooks/useFinanceiroTorneio';

// Sub-aba Resumo do torneio (financeiro-dashboard-liga-torneio, Phase 5, AC-A3/AC-A6) — busca
// seus próprios dados via useFinanceiroTorneio (pages → components → hooks → services → apollo,
// direção única); o gate de permissão já foi resolvido por quem monta este componente
// (TournamentFinanceiroTab → LeagueTournamentManagement). Mirror de
// pages/liga/financeiro/ResumoLigaTab.tsx: mesmos 4 estados obrigatórios (erro de rede com
// retry / indisponível / loading / vazio específico), mesmos data-testid, datetimeUTC:false.

export interface TournamentFinanceiroResumoTabProps {
  torneioId: string;
}

function formatDataBr(iso: string): string {
  return dayjs(iso).format('DD/MM/YYYY');
}

const TournamentFinanceiroResumoTab: React.FC<TournamentFinanceiroResumoTabProps> = ({ torneioId }) => {
  const {
    resumo,
    serie,
    quebra,
    loading,
    error,
    resumoIndisponivel,
    periodo,
    refetch,
    serieLoading,
    serieError,
    refetchSerie,
  } = useFinanceiroTorneio(torneioId);

  // Erro de rede — mensagem amigável + ação de repetir, sem crash e sem catch silencioso.
  if (error) {
    return (
      <div className="text-center py-5" data-testid="resumo-erro">
        <p className="text-danger mb-3">
          Não foi possível carregar o resumo financeiro do torneio. Tente novamente.
        </p>
        <button type="button" className="btn btn-primary" onClick={refetch}>
          Tentar novamente
        </button>
      </div>
    );
  }

  // resumoFinanceiroTorneio retornando null SEM erro (contrato nullable) — estado tratado,
  // distinto do erro de rede.
  if (resumoIndisponivel) {
    return (
      <div className="text-center py-5" data-testid="resumo-indisponivel">
        <p className="text-muted mb-3">O resumo financeiro do torneio não está disponível no momento.</p>
        <button type="button" className="btn btn-primary" onClick={refetch}>
          Tentar novamente
        </button>
      </div>
    );
  }

  if (loading || !resumo) {
    return <div className="text-center py-5">Carregando resumo financeiro...</div>;
  }

  // Mesmas 3 escolhas não-estéticas de ResumoLigaTab.tsx (bar + eixo de tempo real + timestamp
  // local) — ver ai/libs/APEXCHARTS.md §1.
  const chartOptions: ApexOptions = {
    chart: { type: 'bar', toolbar: { show: false } },
    dataLabels: { enabled: false },
    xaxis: { type: 'datetime', labels: { datetimeUTC: false } },
    yaxis: { labels: { formatter: (valor: number) => formatCurrency(valor) } },
    tooltip: { x: { format: 'dd/MM/yyyy' } },
    plotOptions: { bar: { columnWidth: '55%' } },
  };
  const chartSeries = [
    {
      name: 'Recebido por dia',
      data: serie.map((ponto) => ({ x: dayjs(ponto.data).valueOf(), y: ponto.valor })),
    },
  ];

  return (
    <div>
      <div className="mb-3">
        <h5 className="fw-700 mb-1">Resumo financeiro do torneio</h5>
        <span className="text-grey-500 font-xsss">
          Totais calculados no servidor para o período selecionado — nenhum valor é somado no
          cliente.
        </span>
      </div>

      {/* AC-A3: recebido, em aberto, não realizado, estornado, taxa da plataforma e repasse
          líquido (B-11) — todos vindos diretamente do campo do servidor. */}
      <div className="row g-3 mb-4">
        <div className="col-md-4 col-sm-6">
          <KpiCard titulo="Total recebido" valor={resumo.totalRecebido} legenda="Inscrições pagas no período" />
        </div>
        <div className="col-md-4 col-sm-6">
          <KpiCard titulo="Total em aberto" valor={resumo.totalEmAberto} legenda="Inscrições ainda a receber" />
        </div>
        <div className="col-md-4 col-sm-6">
          <KpiCard
            titulo="Total não realizado"
            valor={resumo.totalNaoRealizado}
            legenda="Cancelado, recusado ou expirado"
          />
        </div>
        <div className="col-md-4 col-sm-6">
          <KpiCard titulo="Total estornado" valor={resumo.totalEstornado} legenda="Valores devolvidos a atletas" />
        </div>
        <div className="col-md-4 col-sm-6">
          <KpiCard
            titulo="Taxa da plataforma"
            valor={resumo.totalTaxaPlataforma}
            legenda="Retida pela InPlay no período"
          />
        </div>
        <div className="col-md-4 col-sm-6">
          <KpiCard
            titulo="Repasse líquido"
            valor={resumo.totalRepasseLiga}
            legenda="Valor líquido repassado à liga"
          />
        </div>
      </div>

      {/* A série tem estado ISOLADO do gate geral (useFinanceiroTorneio) — os cards e a quebra
          acima continuam utilizáveis mesmo quando só a 3ª query falha. Mas a falha precisa ser
          apresentada como falha, não como "período sem movimento": as duas mensagens abaixo
          são deliberadamente distintas, e a de erro oferece retry isolado (refetchSerie), sem
          refazer resumo/quebra que já tiveram sucesso. */}
      {serieError ? (
        <div className="card border-0 shadow-xss rounded-xxl p-3 mb-4 text-center py-5" data-testid="resumo-serie-erro">
          <p className="text-danger mb-3">
            Não foi possível carregar o gráfico de recebimentos por dia. Tente novamente.
          </p>
          <button type="button" className="btn btn-primary" onClick={refetchSerie}>
            Tentar novamente
          </button>
        </div>
      ) : serieLoading ? (
        <div className="card border-0 shadow-xss rounded-xxl p-3 mb-4 text-center py-5">
          Carregando gráfico...
        </div>
      ) : serie.length === 0 ? (
        <div className="text-center py-5" data-testid="resumo-serie-vazia">
          <p className="mb-3">
            Nenhum lançamento entre {formatDataBr(periodo.inicio)} e {formatDataBr(periodo.fim)}.
          </p>
          <p className="text-grey-500">Tente ampliar o período para ver mais lançamentos.</p>
        </div>
      ) : (
        <div className="card border-0 shadow-xss rounded-xxl p-3 mb-4">
          <Chart options={chartOptions} series={chartSeries} type="bar" width="100%" />
        </div>
      )}

      {quebra.length > 0 && (
        <div className="card border-0 shadow-xss rounded-xxl p-3">
          <h6 className="fw-700 mb-3">Receita por categoria</h6>
          <ul className="list-unstyled mb-0">
            {quebra.map((linha) => (
              <li
                key={linha.categoriaTorneioId ?? 'sem-categoria'}
                className="d-flex justify-content-between mb-2"
              >
                <span>{linha.categoriaNome ?? 'Sem categoria'}</span>
                <span className="fw-600">{formatCurrency(linha.valor)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default TournamentFinanceiroResumoTab;
